import { Badge, Table, Th, Td } from '../../../school_panel/components/ui';
import type { StaffSchoolTask } from '../../../school_panel/api/types';
import { taskDateLabel, STATUS_HINT, STATUS_LABEL, STATUS_ORDER, type TaskStatus } from '../taskStatus';
import TaskRow, { kindLabel, Owner, TrackerProgress, TaskTitle } from './TaskRow';

export interface StatusedTask { task: StaffSchoolTask; status: TaskStatus }

const DOT: Record<TaskStatus, string> = {
  scheduled: 'bg-on-navy-faint', in_progress: 'bg-slate', follow_up: 'bg-accent', done: 'bg-success',
};
// Design `.pill`: `c-mute` / `st-p` / `p-crit` / `c-up`.
const BADGE_TONE = { scheduled: 'neutral', in_progress: 'info', follow_up: 'danger', done: 'success' } as const;

interface Props { rows: StatusedTask[]; onOpenNotice: (t: StaffSchoolTask) => void; onOpenTracker: (t: StaffSchoolTask) => void }

export function TaskBoard({ rows, onOpenNotice, onOpenTracker }: Props) {
  return (
    <div className="grid grid-cols-1 items-start gap-4 min-[641px]:grid-cols-2 min-[1181px]:grid-cols-4">
      {STATUS_ORDER.map((s) => {
        const items = rows.filter((r) => r.status === s);
        return (
          <section key={s} aria-label={STATUS_LABEL[s]} className="flex min-w-0 flex-col gap-2.5 rounded-[20px] bg-board-col p-3">
            <div className="flex items-center gap-2 px-1.5 pb-1.5 pt-1 font-display text-sm font-bold text-ink">
              <span aria-hidden="true" className={`h-[9px] w-[9px] flex-none rounded-full ${DOT[s]}`} />
              {STATUS_LABEL[s]}
              <span className="rounded-full bg-surface px-2 py-0.5 font-mono text-[11px] font-semibold text-muted-2">{items.length}</span>
              <span className="ml-auto text-xs font-medium text-muted">{STATUS_HINT[s]}</span>
            </div>
            {items.length === 0
              ? <p className="px-2 py-4 text-center text-xs text-muted">Nothing here</p>
              : items.map(({ task }) => <TaskRow key={task.id} task={task} status={s} onOpenNotice={onOpenNotice} onOpenTracker={onOpenTracker} />)}
          </section>
        );
      })}
    </div>
  );
}

export function TaskTable({ rows, onOpenNotice, onOpenTracker }: Props) {
  return (
    <Table minWidth={860} className="text-sm">
      <thead>
        <tr>
          {['Task', 'Type', 'Audience', 'Due'].map((h) => <Th key={h}>{h}</Th>)}
          <Th className="w-[18%]">Progress</Th>
          <Th>Status</Th>
          <Th>Owner</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ task: t, status }) => {
          const date = taskDateLabel(t, status);
          return (
            <tr key={t.id}>
              <Td>
                <TaskTitle task={t} onOpenNotice={onOpenNotice} />
                <span className="block text-xs text-muted">{t.subject?.name ?? 'Notice'}</span>
              </Td>
              <Td><span className="font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-ink">{kindLabel(t)}</span></Td>
              <Td className="text-ink">{t.audience_label}</Td>
              <Td className="whitespace-nowrap">
                {date && <span className={`font-mono text-xs font-semibold ${status === 'follow_up' ? 'text-danger-ink' : 'text-muted-2'}`}>{date}</span>}
              </Td>
              <Td><TrackerProgress task={t} status={status} wide onOpenTracker={onOpenTracker} /></Td>
              <Td><Badge tone={BADGE_TONE[status]} className={status === 'scheduled' ? '!text-muted-2' : ''}>{STATUS_LABEL[status]}</Badge></Td>
              <Td><Owner name={t.created_by} named /></Td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
